import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import Table, { ColumnsType } from 'antd/es/table';
import { getMemberPointHistoryList } from 'apis/ums';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18n';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import PointHistoryTrend from 'pages/ums/point-history-trend';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import formatTime from 'utils/formatTime';
import getClientTimeZone from 'utils/getClientTimeZone';

type PointHistory = Awaited<
  ReturnType<typeof getMemberPointHistoryList>
>['list'][number];

const OPERATE_TYPE_OPTIONS = [
  { label: '下单', value: 1 },
  { label: '系统操作', value: 4 },
  { label: '会员取消订单', value: 5 },
  { label: '管理员取消订单', value: 6 },
  { label: '系统取消订单', value: 7 },
  { label: '会员确认收货', value: 9 },
  { label: '管理员完成订单', value: 10 },
  { label: '积分过期', value: 11 },
  { label: '活动赠送', value: 12 },
  { label: '后台调整', value: 13 },
] as const;

const OPERATE_TYPE_LABEL_MAP: Record<number, string> = Object.fromEntries(
  OPERATE_TYPE_OPTIONS.map((item) => [item.value, item.label]),
);

const formatPoint = (value?: number | null) => {
  if (value == null) return '-';
  const amount = Number(value);
  const text = Math.abs(amount).toLocaleString();
  if (amount > 0) return `+${text}`;
  if (amount < 0) return `-${text}`;
  return '0';
};

const useQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [direction, setDirection] = useQueryState(
    'direction',
    parseAsString.withDefault(''),
  );
  const [operateType, setOperateType] = useQueryState(
    'operateType',
    parseAsInteger,
  );
  const [memberId, setMemberId] = useQueryState('memberId', parseAsInteger);
  const [memberKeyword, setMemberKeyword] = useQueryState(
    'memberKeyword',
    parseAsString.withDefault(''),
  );
  const [orderSn, setOrderSn] = useQueryState(
    'orderSn',
    parseAsString.withDefault(''),
  );
  const [operateMan, setOperateMan] = useQueryState(
    'operateMan',
    parseAsString.withDefault(''),
  );
  const [startTime, setStartTime] = useQueryState(
    'startTime',
    parseAsString.withDefault(''),
  );
  const [endTime, setEndTime] = useQueryState(
    'endTime',
    parseAsString.withDefault(''),
  );

  return {
    pageNum,
    pageSize,
    direction,
    operateType,
    memberId,
    memberKeyword,
    orderSn,
    operateMan,
    startTime,
    endTime,
    setPageNum,
    setPageSize,
    setDirection,
    setOperateType,
    setMemberId,
    setMemberKeyword,
    setOrderSn,
    setOperateMan,
    setStartTime,
    setEndTime,
  };
};

const PointHistoryPage = () => {
  const {
    pageNum,
    pageSize,
    direction,
    operateType,
    memberId,
    memberKeyword,
    orderSn,
    operateMan,
    startTime,
    endTime,
    setPageNum,
    setPageSize,
    setDirection,
    setOperateType,
    setMemberId,
    setMemberKeyword,
    setOrderSn,
    setOperateMan,
    setStartTime,
    setEndTime,
  } = useQueryParams();

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<PointHistory>();

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);
      const { list, total } = await getMemberPointHistoryList({
        pageNum,
        pageSize,
        direction:
          direction === 'grant' || direction === 'use' ? direction : undefined,
        operateType: operateType ?? undefined,
        memberId: memberId ?? undefined,
        memberKeyword: memberKeyword || undefined,
        orderSn: orderSn || undefined,
        operateMan: operateMan || undefined,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        timeZone: getClientTimeZone(),
      });
      setDataSource(list);
      setTotal(total);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [
    direction,
    endTime,
    memberId,
    memberKeyword,
    operateMan,
    operateType,
    orderSn,
    pageNum,
    pageSize,
    setDataSource,
    setLoading,
    setTotal,
    startTime,
  ]);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const dateRange = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    if (!startTime && !endTime) return null;
    return [
      startTime ? dayjs(startTime) : null,
      endTime ? dayjs(endTime) : null,
    ];
  }, [endTime, startTime]);

  const columns: ColumnsType<PointHistory> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 90,
      },
      {
        title: '时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 180,
        render: (createTime?: string) => formatTime(createTime),
      },
      {
        title: '会员',
        key: 'member',
        width: 220,
        render: (_, record) => {
          const name = record.memberEmail || record.memberUsername || '-';
          return (
            <>
              <Link to={`/ums/member-view/${record.memberId}`}>{name}</Link>
              <span className="text-gray-400"> (#{record.memberId})</span>
            </>
          );
        },
      },
      {
        title: '方向',
        dataIndex: 'changeCount',
        key: 'direction',
        width: 90,
        render: (changeCount: number) => {
          if (changeCount > 0) return <Tag color="blue">发放</Tag>;
          if (changeCount < 0) return <Tag color="orange">使用</Tag>;
          return <Tag>无变动</Tag>;
        },
      },
      {
        title: '变动',
        dataIndex: 'changeCount',
        key: 'changeCount',
        width: 120,
        render: (changeCount: number) => (
          <span
            className={changeCount < 0 ? 'text-orange-500' : 'text-blue-600'}
          >
            {formatPoint(changeCount)}
          </span>
        ),
      },
      {
        title: '类型',
        dataIndex: 'operateType',
        key: 'operateType',
        width: 140,
        render: (operateType: number) =>
          OPERATE_TYPE_LABEL_MAP[operateType] || `类型 ${operateType}`,
      },
      {
        title: '订单',
        key: 'order',
        width: 180,
        render: (_, record) => {
          if (!record.orderId) return '-';
          return (
            <Link to={`/oms/order-view/${record.orderId}`}>
              {record.orderId}
            </Link>
          );
        },
      },
      {
        title: '操作人',
        dataIndex: 'operateMan',
        key: 'operateMan',
        width: 140,
        render: (value?: string | null) => value || '-',
      },
      {
        title: '备注',
        dataIndex: 'operateNote',
        key: 'operateNote',
        width: 280,
        render: (value?: string | null) =>
          value ? (
            <Typography.Paragraph
              className="!mb-0 whitespace-pre-wrap"
              ellipsis={{ rows: 2, tooltip: value }}
            >
              {value}
            </Typography.Paragraph>
          ) : (
            '-'
          ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PointHistoryTrend />

      <Card bodyStyle={{ padding: '16px' }}>
        <Form layout="inline">
          <Form.Item label="方向" className="w-48">
            <Select
              value={direction || undefined}
              onChange={(value) => {
                setDirection(value || '');
                setPageNum(1);
              }}
              allowClear
              options={[
                { label: '发放', value: 'grant' },
                { label: '使用', value: 'use' },
              ]}
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
            />
          </Form.Item>

          <Form.Item label="类型" className="w-56">
            <Select
              value={operateType ?? undefined}
              onChange={(value) => {
                setOperateType(typeof value === 'number' ? value : null);
                setPageNum(1);
              }}
              allowClear
              options={[...OPERATE_TYPE_OPTIONS]}
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
            />
          </Form.Item>

          <Form.Item label="会员 ID">
            <InputNumber
              className="w-40"
              value={memberId ?? undefined}
              onChange={(value) => {
                setMemberId(typeof value === 'number' ? value : null);
                setPageNum(1);
              }}
              min={1}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>

          <Form.Item label="会员">
            <Input
              value={memberKeyword}
              onChange={(e) => {
                setMemberKeyword(e.target.value);
                setPageNum(1);
              }}
              placeholder="邮箱 / 用户名"
              allowClear
            />
          </Form.Item>

          <Form.Item label="订单号">
            <Input
              value={orderSn}
              onChange={(e) => {
                setOrderSn(e.target.value);
                setPageNum(1);
              }}
              allowClear
            />
          </Form.Item>

          <Form.Item label="操作人">
            <Input
              value={operateMan}
              onChange={(e) => {
                setOperateMan(e.target.value);
                setPageNum(1);
              }}
              allowClear
            />
          </Form.Item>

          <Form.Item label="时间">
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(dates) => {
                setStartTime(
                  dates?.[0]
                    ? dates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss')
                    : '',
                );
                setEndTime(
                  dates?.[1]
                    ? dates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
                    : '',
                );
                setPageNum(1);
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                htmlType="button"
                onClick={() => {
                  setDirection('');
                  setOperateType(null);
                  setMemberId(null);
                  setMemberKeyword('');
                  setOrderSn('');
                  setOperateMan('');
                  setStartTime('');
                  setEndTime('');
                  setPageNum(1);
                  setPageSize(10);
                }}
              >
                <Trans i18nKey={LOCALS.reset} />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: '16px' }}>
        <Table
          bordered
          pagination={{
            total,
            pageSize,
            current: pageNum,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            onChange: (page, nextPageSize) => {
              setPageNum(page);
              setPageSize(nextPageSize);
            },
          }}
          loading={loading}
          rowKey="id"
          dataSource={dataSource}
          columns={columns}
          scroll={{ x: 'max-content' }}
          tableLayout="auto"
        />
      </Card>
    </div>
  );
};

export default PointHistoryPage;
